import React, { useContext, useRef } from 'react';
import classes from './MediaLibraryModalLayout.module.scss';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import classNames from 'classnames';
import { useDimensions } from '../../../hooks/useDimensions';
import { MediaGrid } from './MediaGrid';
import { Loader } from '../../../components/containers/Loader';
import { BreakpointsEnum } from '../../../constants/breakpoints';
import { Button } from '../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { IconsEnum } from '../../../constants/icons';
import { ButtonMenu } from '../../../components/actions/ButtonMenu';
import { MediaLibraryFilterMenu } from './MediaLibraryFilterMenu';
import { FilterMenuContextType } from './MediaLibraryFilterMenu/MediaLibraryFilterMenu.view';
import { MediaLibraryViewProps } from './MediaLibrary.view';
import { MediaGridContext } from './MediaGrid/MediaGrid.view';
import { PostEditorGroupDataContext } from '../../PostEditor/states/PostEditorGroupDataProvider';
import { ActiveCustomizeIndexContext } from '../../PostEditor/states/ActiveCustomizeIndexProvider';
import { MediaItem } from '../../../models/media';
import { PostEditorMediaAddFromLibAction } from '../../PostEditor/states/PostEditorActions';
import { AddMediaSelectorModal } from './AddMediaSelectorModal';
import { Modal } from '../../../components/containers/Modal';
import { FindImagesModal } from './FindImagesModal';
import { FileWithPreview } from './AddMediaSelectorModal/AddMediaSelectorModal.view';
import { AppContext } from '../../../providers/AppProvider';
import { showToast } from '../../../helpers/showToast';
import { GENERIC_ERROR_MESSAGE } from '../../../constants/apollo-errors';
import { AddMediaByUrlModal } from './AddMediaByUrlModal';
import { DesignInCanvaModal } from './DesignInCanvaModal';
import { MediaLibraryEmptyPlaceholder } from './MediaLibraryEmptyPlaceholder';
import { GetMediaLibraryDocument } from '../../../models/graphql';
import { PostEditorContext } from '../../PostEditor/states/PostEditorProvider';
import { useLocation } from 'react-router-dom';
import { BeaconContext } from '../../../providers/BeaconProvider';
import { MediaImageFromLink } from '../../PostEditor/MediaImageFromLink';
import { useApolloClient } from '@apollo/client';

export type MediaLibraryModalLayoutViewProps = MediaLibraryViewProps & {
  gridContext: MediaGridContext;
};

export const MediaLibraryModalLayoutView = (props: MediaLibraryModalLayoutViewProps): JSX.Element => {
  const apolloClient = useApolloClient();
  const rightColRef = useRef(null);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const { initialized: isInPostEditor } = useContext(PostEditorContext);
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const { selectedWorkspaceId } = useContext(AppContext);
  const location = useLocation();
  const { beacon } = useContext(BeaconContext);
  const breakpoint = useBreakpoints();

  const rightColumnWrapperClass = classNames(classes.RightSideColumn, {
    [classes.EmptyState]: props.mediaItems.length === 0
  });
  const leftColumnWrapperClass = classNames(classes.LeftSideWrapper, 'cell', 'show-for-large');
  const leftColumnClass = classNames(classes.LeftSideColumn);
  const { width } = useDimensions(rightColRef, 150);

  const primaryButtonAction = (media: MediaItem[]) => {
    const validMediaItems = media.filter(
      item =>
        !props.imagesIdsWithErrorSelected.some(errorItem => errorItem.id === item.id && errorItem.type === item.type)
    );

    const payload: PostEditorMediaAddFromLibAction['payload'] = {
      postGroupIndex,
      customizeIndex,
      media: validMediaItems
    };

    props.dispatchPostEditor({ type: 'mediaAddFromLib', payload });
    props.onCloseHandler && props.onCloseHandler();
  };

  const secondaryButtonAction = () => {
    props.deleteMediaHandler(props.imagesIdsWithErrorSelected);
  };

  const fileUploadHandler = (files: FileWithPreview[]) => {
    props.dispatchPostEditor({
      type: 'mediaUpload',
      payload: { postGroupIndex, files, customizeIndex, queueUploads: props.queueUploads, selectedWorkspaceId }
    });
    props.onCloseHandler && props.onCloseHandler();
  };

  const shareFromFindImagesHandler = (media: MediaItem[]) => {
    props.dispatchPostEditor({
      type: 'mediaUploadFromThirdParty',
      payload: { postGroupIndex, customizeIndex, selectedWorkspaceId, media, queueUploads: props.queueUploads }
    });
    props.onCloseHandler && props.onCloseHandler();
  };

  const AddMediaByUrlHandler = (url: string) => {
    props.dispatchPostEditor({
      type: 'mediaPasteFromUrl',
      payload: { postGroupIndex, customizeIndex, selectedWorkspaceId, url, queueUploads: props.queueUploads, beacon }
    });
    props.onCloseHandler && props.onCloseHandler();
  };

  const AddMediaFromLinkHandler = (urls: string[]) => {
    props.dispatchPostEditor({
      type: 'mediaAddFromLink',
      payload: { postGroupIndex, customizeIndex, selectedWorkspaceId, urls, queueUploads: props.queueUploads, beacon }
    });
    props.onCloseHandler && props.onCloseHandler();
  };

  const AddMediaByCanvaHandler = (url: string, canvaId: string) => {
    if (!selectedWorkspaceId) {
      showToast({
        severity: 'error',
        detail: GENERIC_ERROR_MESSAGE,
        summary: 'Error'
      });
      return;
    }
    props.dispatchPostEditor({
      type: 'mediaFromCanva',
      payload: { postGroupIndex, customizeIndex, selectedWorkspaceId, url, canvaId, queueUploads: props.queueUploads }
    });
    props.onCloseHandler && props.onCloseHandler();
  };

  const modalFilterButtonMenuClass = classNames('show-for-small hide-for-large margin-bottom-small-16');
  const hasFilters = !!(props.filters.origin || props.filters.type || props.filters.proportion);

  const mediaGrid = (
    <>
      {!props.loading ? (
        props.mediaItems.length ? (
          <MediaGrid
            mediaItems={props.mediaItems}
            primaryButtonAction={primaryButtonAction}
            primaryButtonTitle={'Add to Post'}
            secondaryButtonAction={
              Object.keys(props.selectedMediaItems).length === props.imagesIdsWithErrorSelected.length
                ? () => secondaryButtonAction()
                : undefined
            }
            secondaryButtonTitle={'delete'}
            context={props.gridContext}
            parentWidth={width}
            selectedMediaItems={props.selectedMediaItems}
            setSelectedMediaItems={props.setSelectedMediaItems}
            setImagesIdsWithErrorSelected={props.setImagesIdsWithErrorSelected}
            imagesIdsWithErrorSelected={props.imagesIdsWithErrorSelected}
          />
        ) : (
          <MediaLibraryEmptyPlaceholder
            buttonAction={() => props.setShowAddFromLibraryModal(false)}
            hasFilters={hasFilters}
          />
        )
      ) : (
        <div className={classes.LoaderWrapperHeight}>
          <Loader variation={'dark'} size={'large'} />
        </div>
      )}
    </>
  );

  return (
    <>
      <AddMediaSelectorModal
        context={'post_editor'}
        addFromLibraryClickHandler={() => {
          props.setShowAddFromLibraryModal(true);
        }}
        onClose={() => props.onCloseHandler && props.onCloseHandler()}
        fileUploadHandler={fileUploadHandler}
        mediaByUrlClickHandler={() => props.setShowAddMediaByUrlModal(true)}
        findImagesClickHandler={() => props.setShowFindImagesModal(true)}
        designInCanvaClickHandler={() => props.setDesignInCanvaModal(true)}
        thumbnailSize={180}>
        <MediaImageFromLink uploadHandler={AddMediaFromLinkHandler} />
      </AddMediaSelectorModal>
      {props.showAddFromLibraryModal && (
        <Modal
          onClose={() => {
            props.setShowAddFromLibraryModal(false);

            // Only do this if we are closing the Post Editor's ML modal
            if (isInPostEditor && location.pathname === '/publish/media') {
              apolloClient.refetchQueries({
                include: [GetMediaLibraryDocument]
              });
            }
          }}
          scrollToBottomHandler={props.fetchMoreMediaItemsHandler}
          showCloseIcon={true}
          size={'fluid'}
          title={'Add Media from Library'}>
          <div className={modalFilterButtonMenuClass}>
            {breakpoint === BreakpointsEnum.small ||
            breakpoint === BreakpointsEnum.medium ||
            breakpoint === BreakpointsEnum.tabletportrait ? (
              <>
                <Button
                  type={'button'}
                  variant={'hollow'}
                  size={'medium'}
                  color={ButtonColorsEnum.primary}
                  action={() => props.setShowFilterModal(!props.showFilterModal)}
                  icon={{ side: 'left', value: IconsEnum.filter }}>
                  Filters
                </Button>
              </>
            ) : (
              <ButtonMenu
                isSquareButton={false}
                side={'left'}
                icon={IconsEnum.filter}
                dropdownMaximize={true}
                iconSide={'left'}
                elementType={'button'}
                label={'Filters'}
                size={'medium'}
                variant={'hollow'}>
                <div className={classes.ModalFilterButtonMenu}>
                  <MediaLibraryFilterMenu
                    filters={props.filters}
                    dispatchFilters={props.dispatchFilters}
                    context={FilterMenuContextType.DROPDOWN}
                  />
                </div>
              </ButtonMenu>
            )}
          </div>
          <div className={leftColumnWrapperClass}>
            <div className={leftColumnClass}>
              <div>
                <MediaLibraryFilterMenu
                  filters={props.filters}
                  dispatchFilters={props.dispatchFilters}
                  context={FilterMenuContextType.MODAL}
                />
              </div>
            </div>
          </div>
          <div className={rightColumnWrapperClass} ref={rightColRef}>
            <>
              {mediaGrid}
              {props.loadingMore && (
                <div className={classes.loadMoreContainer}>
                  <div className={classes.LoadingMore}>
                    <Loader variation={'dark'} size={'small'} type={'inline'} />
                  </div>
                </div>
              )}
            </>
          </div>
        </Modal>
      )}
      {props.showAddMediaByUrlModal && (
        <AddMediaByUrlModal onClose={() => props.setShowAddMediaByUrlModal(false)} onSubmit={AddMediaByUrlHandler} />
      )}
      {props.showFindImagesModal && (
        <FindImagesModal
          shareItemAction={shareFromFindImagesHandler}
          context={MediaGridContext.POST_EDITOR_FIND_IMAGES}
          onClose={() => props.setShowFindImagesModal(false)}
          fileUploadHandler={() => {
            /* TODO Is this needed? */
          }}
          setImagesIdsWithErrorSelected={props.setImagesIdsWithErrorSelected}
          imagesIdsWithErrorSelected={props.imagesIdsWithErrorSelected}
        />
      )}
      {props.showDesignInCanvaModal && (
        <DesignInCanvaModal
          onCloseHandler={() => props.setDesignInCanvaModal(false)}
          fileUploadHandlerPostEditor={AddMediaByCanvaHandler}
          publishLabel={'Add to Post'}
        />
      )}
    </>
  );
};
